import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Minus, Trash2, MapPin, CheckCircle2, Shirt, ShieldCheck, CreditCard } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const eur = (n) => `€ ${Number(n).toFixed(2)}`;
const sizesOf = (p) => (p?.sizes || "").split(",").map((s) => s.trim().toUpperCase()).filter(Boolean);

export const ShopMerch = () => {
  const [product, setProduct] = useState(null);
  const [lines, setLines] = useState([{ size: "", quantity: 1 }]);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "" });
  const [privacy, setPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/shop/products`).then((r) => r.json()).then((d) => {
      const p = Array.isArray(d) ? d[0] : null;
      setProduct(p || null);
      if (p) { const s = sizesOf(p); setLines([{ size: s[0] || "", quantity: 1 }]); }
    }).catch(() => setProduct(null));
  }, []);

  const sizes = sizesOf(product);
  const total = useMemo(() => lines.reduce((s, l) => s + (product?.price || 0) * l.quantity, 0), [lines, product]);

  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, { size: sizes[0] || "", quantity: 1 }]);
  const removeLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));
  const setField = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const payload = () => ({
    items: lines.map((l) => ({ product_id: product.id, size: l.size || null, quantity: l.quantity })),
    first_name: form.first_name.trim(), last_name: form.last_name.trim(),
    email: form.email.trim(), phone: form.phone.trim(), privacy_accepted: privacy,
  });

  const validate = () => {
    if (!product) return false;
    if (sizes.length && lines.some((l) => !l.size)) { toast.error("Seleziona la taglia per ogni maglietta."); return false; }
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Compila nome, cognome, email e telefono."); return false;
    }
    if (!privacy) { toast.error("Accetta l'informativa sul trattamento dei dati."); return false; }
    return true;
  };

  const checkout = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/shop/order/revolut`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload()) });
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.detail || "Errore nell'invio dell'ordine.");
      setDone(d);
      window.open(d.revolut_url, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(e.message || "Riprova."); } finally { setLoading(false); }
  };

  if (done) return (
    <section id="shop" data-testid="shop-section" className="py-20 sm:py-28 bg-white">
      <div className="max-w-xl mx-auto px-6 text-center">
        <div className="rounded-3xl bg-[#2E4F3B] text-white p-10">
          <CheckCircle2 className="w-14 h-14 text-[#E8C9A0] mx-auto mb-4" />
          <h2 className="font-display text-2xl font-black">Ordine ricevuto!</h2>
          <p className="mt-2 text-[#E8C9A0] font-bold" data-testid="order-number">N. ordine: {done.order_number}</p>
          <div className="mt-4 text-left bg-white/10 rounded-2xl p-4 text-sm">
            {(done.items || []).map((l, i) => (
              <div key={i} className="flex justify-between py-0.5"><span>{l.quantity}× Maglietta{l.size ? ` · ${l.size}` : ""}</span><span>{eur(l.line_total)}</span></div>
            ))}
            <div className="flex justify-between pt-2 mt-2 border-t border-white/20 font-bold"><span>Totale</span><span>{eur(done.total)}</span></div>
          </div>
          <p className="mt-4 text-white/90 leading-relaxed text-sm">
            Per completare l'ordine, effettua il pagamento di <strong className="text-[#E8C9A0]">{eur(done.total)}</strong> su Revolut. Se la scheda non si è aperta, usa il pulsante qui sotto.
          </p>
          <a href={done.revolut_url} target="_blank" rel="noopener noreferrer" data-testid="shop-revolut-pay"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C15C3D] hover:bg-[#a64830] text-white font-bold px-6 py-4 transition-colors shadow-lg">
            <CreditCard className="w-5 h-5" /> Paga {eur(done.total)} su Revolut
          </a>
          <p className="mt-3 text-xs text-white/70 leading-relaxed">Nella pagina Revolut inserisci l'importo di <strong>{eur(done.total)}</strong> e indica il n. ordine {done.order_number}. Ti abbiamo inviato anche un'email con il riepilogo.</p>
          <button data-testid="shop-new-order" onClick={() => { setDone(null); setPrivacy(false); }} className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/15 hover:bg-white/25 text-white font-bold px-6 py-3 transition-colors">Nuovo ordine</button>
        </div>
      </div>
    </section>
  );

  return (
    <section id="shop" data-testid="shop-section" className="py-20 sm:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="max-w-2xl mb-10">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.22em] text-[#C15C3D] mb-4">Shop</span>
          <h2 data-testid="shop-title" className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-[#1A1A1A] leading-[1.05] tracking-tight">
            Indossa <span className="text-[#2E4F3B] italic font-light">Per Asciano.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#5C5C5C] leading-relaxed">
            L'acquisto della maglietta sostiene le attività dell'associazione sul territorio. <strong className="text-[#1A1A1A]">Ritiro all'edicola della Patrizia ad Asciano</strong> (nessuna spedizione).
          </p>
        </div>

        {!product ? (
          <div className="flex items-center justify-center py-16 text-[#5C5C5C]"><Loader2 className="w-5 h-5 animate-spin mr-2" />Caricamento…</div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            {/* Product showcase */}
            <div data-testid="shop-product" className="rounded-3xl border-2 border-[#E5E1D8] overflow-hidden bg-[#FBF9F6]">
              <div className="aspect-square bg-white flex items-center justify-center overflow-hidden">
                {product.image_url ? <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" /> : <Shirt className="w-24 h-24 text-[#C7C2B6]" strokeWidth={1.1} />}
              </div>
              <div className="p-6">
                <h3 className="font-display text-2xl font-black text-[#1A1A1A]">{product.name}</h3>
                {product.description && <p className="mt-2 text-sm text-[#5C5C5C] leading-relaxed">{product.description}</p>}
                <div className="mt-4 flex items-center gap-4">
                  <span className="font-display text-3xl font-black text-[#2E4F3B]">{eur(product.price)}</span>
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#2E4F3B] bg-[#2E4F3B]/10 px-3 py-1 rounded-full"><CheckCircle2 className="w-3.5 h-3.5" /> Disponibile</span>
                </div>
                {sizes.length > 0 && <div className="mt-3 text-xs text-[#5C5C5C]">Taglie disponibili: <strong className="text-[#1A1A1A]">{sizes.join(" · ")}</strong></div>}
              </div>
            </div>

            {/* Order form */}
            <div className="rounded-3xl border-2 border-[#E5E1D8] bg-white p-6 sm:p-8">
              <h3 className="font-display text-xl font-black text-[#1A1A1A] mb-4">Componi il tuo ordine</h3>

              <div className="space-y-3" data-testid="order-lines">
                {lines.map((l, i) => (
                  <div key={i} data-testid={`order-line-${i}`} className="flex items-center gap-2 bg-[#FBF9F6] border border-[#E5E1D8] rounded-2xl p-3">
                    <Shirt className="w-5 h-5 text-[#2E4F3B] shrink-0" />
                    {sizes.length > 0 ? (
                      <select data-testid={`line-${i}-size`} value={l.size} onChange={(e) => setLine(i, { size: e.target.value })} className="flex-1 bg-white border-2 border-[#E5E1D8] rounded-lg px-3 py-2 text-sm font-semibold focus:outline-none focus:border-[#2E4F3B]">
                        {sizes.map((s) => <option key={s} value={s}>Taglia {s}</option>)}
                      </select>
                    ) : <span className="flex-1 text-sm">Taglia unica</span>}
                    <div className="inline-flex items-center gap-1 bg-white border-2 border-[#E5E1D8] rounded-lg p-0.5">
                      <button type="button" data-testid={`line-${i}-minus`} onClick={() => setLine(i, { quantity: Math.max(1, l.quantity - 1) })} className="w-8 h-8 rounded flex items-center justify-center hover:bg-[#FBF9F6]"><Minus className="w-3.5 h-3.5" /></button>
                      <span className="w-6 text-center text-sm font-bold tabular-nums">{l.quantity}</span>
                      <button type="button" data-testid={`line-${i}-plus`} onClick={() => setLine(i, { quantity: Math.min(20, l.quantity + 1) })} className="w-8 h-8 rounded flex items-center justify-center hover:bg-[#FBF9F6]"><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                    {lines.length > 1 && <button type="button" data-testid={`line-${i}-remove`} onClick={() => removeLine(i)} className="p-1.5 text-[#C15C3D] hover:bg-[#C15C3D]/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                ))}
              </div>
              <button type="button" data-testid="add-line" onClick={addLine} className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-[#2E4F3B] hover:gap-3 transition-all"><Plus className="w-4 h-4" /> Aggiungi un'altra taglia</button>

              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <input data-testid="shop-first-name" name="first_name" value={form.first_name} onChange={setField} placeholder="Nome *" className="w-full bg-white border-2 border-[#E5E1D8] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#2E4F3B]" />
                <input data-testid="shop-last-name" name="last_name" value={form.last_name} onChange={setField} placeholder="Cognome *" className="w-full bg-white border-2 border-[#E5E1D8] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#2E4F3B]" />
                <input data-testid="shop-phone" name="phone" type="tel" value={form.phone} onChange={setField} placeholder="Telefono *" className="w-full bg-white border-2 border-[#E5E1D8] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#2E4F3B]" />
                <input data-testid="shop-email" name="email" type="email" value={form.email} onChange={setField} placeholder="Email *" className="w-full bg-white border-2 border-[#E5E1D8] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#2E4F3B]" />
              </div>

              {/* Summary */}
              <div className="mt-5 rounded-2xl bg-[#FBF9F6] border border-[#E5E1D8] p-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C5C5C] mb-2">Riepilogo</div>
                {lines.map((l, i) => (
                  <div key={i} className="flex justify-between text-sm py-0.5"><span>{l.quantity}× Maglietta{l.size ? ` · ${l.size}` : ""}</span><span className="tabular-nums">{eur(product.price * l.quantity)}</span></div>
                ))}
                <div className="flex justify-between pt-2 mt-2 border-t-2 border-[#E5E1D8]"><span className="font-bold">Totale</span><span data-testid="order-total" className="font-display text-xl font-black tabular-nums">{eur(total)}</span></div>
              </div>

              <label className="mt-4 flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" data-testid="shop-privacy" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="mt-1 w-4 h-4 accent-[#2E4F3B]" />
                <span className="text-xs text-[#5C5C5C] leading-relaxed">
                  Acconsento al trattamento dei miei dati personali per la sola gestione dell'ordine e del ritiro. I dati non saranno usati per newsletter o comunicazioni promozionali.
                </span>
              </label>

              <div className="mt-3 flex items-start gap-2 rounded-xl bg-[#2E4F3B]/8 border border-[#2E4F3B]/20 p-3">
                <MapPin className="w-4 h-4 text-[#2E4F3B] shrink-0 mt-0.5" />
                <p className="text-xs text-[#1A1A1A] leading-relaxed">Ritiro all'edicola della Patrizia ad Asciano. Riceverai un'email quando l'ordine sarà pronto.</p>
              </div>

              <div className="mt-5">
                <button type="button" data-testid="shop-checkout-btn" disabled={loading} onClick={checkout}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C15C3D] hover:bg-[#a64830] disabled:opacity-60 text-white font-bold px-6 py-4 transition-colors shadow-lg">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />} Ordina e paga su Revolut · {eur(total)}
                </button>
                <div className="mt-3 flex items-start gap-2 rounded-xl bg-[#2E4F3B]/8 border border-[#2E4F3B]/20 p-3">
                  <CreditCard className="w-4 h-4 text-[#2E4F3B] shrink-0 mt-0.5" />
                  <p className="text-xs text-[#1A1A1A] leading-relaxed">Registriamo il tuo ordine e ti portiamo su <strong>Revolut</strong> per il pagamento di {eur(total)}. Riceverai anche un'email con il riepilogo e il link.</p>
                </div>
                <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-[#9a9a9a]"><ShieldCheck className="w-3.5 h-3.5" /> Ritiro all'edicola della Patrizia · nessuna spedizione</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
